<template>
    <dashboard-container>

        <article v-if="!loading && daysUntilTrialEnds" class="w-full px-3 py-3 rounded-lg text-base font-semibold text-secondary bg-secondary bg-opacity-10 text-center">

                The model trial period ends 
                <hover-text
                    :default="daysUntilTrialEnds"
                    :hover="trialEndsDate"
                    tag="span"
                />
                . To avoid being billed, <a class="cursor-pointer underline"  @click="stopModelConfirmationModalOpen = true">delete this model</a>.

        </article>

        <dashboard-section header="Your model">

            <template v-slot:additional>
                <button @click="stopModelConfirmationModalOpen = true" class="underline transition duration-150 text-error dark:text-red-400 hover:text-red-400 focus:outline-none focus:shadow-outline rounded-sm flex items-center">
                    
                    <icon-svg icon="delete" class="w-4 mr-3"/>

                    Delete Model</button>
            </template>

            <div class="flex flex-col space-y-4" v-if="currentModel">
                
                <router-link :to="{ name: 'UploadModel', params: { ...$route.params }, query: { ...$route.query }}" v-if="currentModel && currentModel.status === 'created'" class="px-5 py-3 rounded-lg border border-dashed border-gray-300 bg-gray-100  dark:bg-gray-800 w-full flex items-center justify-between focus:outline-none focus:shadow-outline rounded-lg">

                        <div class="flex items-center space-x-4">
                                    <icon-svg icon="model" class="w-5 text-gray-600"/>
                                    <typography variant="p" class="font-semibold text-gray-600">Upload your model...</typography>
                        </div>

                        <icon-svg icon="thin-arrow-right" class="w-5 text-gray-600"/>

                </router-link>

            </div>

            <div v-if="currentModel && currentModel.status === 'deployed'" class="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-4 flex flex-col">

                <span class="text-sm text-gray-500 dark:text-gray-600">Available via</span>
                <typography variant="p" class="font-semibold uppercase text-gray-700 dark:text-gray-300">POST</typography>

                <span class="text-sm text-gray-500 dark:text-gray-600 mt-6">Endpoint</span>
                <typography variant="p" class="font-semibold text-gray-700 dark:text-gray-300">{{endpoint}}</typography>

                <span class="text-sm text-gray-500 dark:text-gray-600 mt-6">Signatures</span>
                <typography variant="p" class="font-semibold text-gray-700 dark:text-gray-300">{{currentModel.signatures}}</typography>

                <span class="text-sm text-gray-500 dark:text-gray-600 mt-6">Request body</span>
    
                <div class="mt-3 max-w-lg flex flex-col space-y-4">
                    <v-jsoneditor v-model="payload" :options="{
                        mode: 'code',
                        indentation: 4,
                        mainMenuBar: false
                    }" height="150px" :plus="false"/>

                    <base-button @click="testEndpoint" class="self-end" size="small" bgColor="bg-gray-700" borderColor="border-gray-700" hoverBg="hover:bg-gray-600" hoverBorder="hover:border-gray-500">Test Endpoint</base-button>

                </div>

                <span v-if="response" class="text-sm text-gray-500 dark:text-gray-600 mt-8 flex items-center justify-between space-x-2 max-w-lg">
                    <p>Response - {{ responseTime }}</p>

                    <button @click="copyResponse" class="text-secondary focus:outline-none">{{ copyText }}</button>
                </span>
    
                <div v-if="response" class="mt-3 max-w-lg flex flex-col space-y-4">
        
                    <v-jsoneditor v-model="response" :options="{
                        mode: 'preview',
                        indentation: 4,
                        mainMenuBar: false
                    }"  height="150px" :plus="false"/>

                </div>

                <span class="text-sm text-gray-500 dark:text-gray-600 mt-6">Quick code</span>

                <!-- view for code snippets -->
            </div>

            <is-deploying v-if="deploying"/>

        </dashboard-section>

        <dashboard-section header="Recent activity"  v-if="currentModel && currentModel.status !== 'created'">

            <activity-table :activity="currentModel.activity"/>

        </dashboard-section>

        <modal @action="stopModel" @close="stopModelConfirmationModalOpen = false" 
          :open="stopModelConfirmationModalOpen" cta="Confirm Model Deletion" title="Are you sure?" error>
              <p>This will delete your model container and cancel any billing asssociated. Just checking, as this can't be undone.</p>
        </modal>


    </dashboard-container>
</template>

<script>
import { LayoutComponents } from '@/modules/Dashboard/components'
import { createModel, uploadedModelImage, stopModel } from "@/graphql/mutations";
import { uploadModelImage } from "@/graphql/queries";
import { currentProject, loading, error } from '@/mixins'
import axios from 'axios'
import moment from 'moment'

export default {
    name: "ModelPage",
    metaInfo() {
        return {
            title: this.currentModel ? this.currentModel.name : 'Model'
        }
    },
    data () {
        return {
            payload: { "payload": [] },
            response: null,
            responseTime: null,
            stopModelConfirmationModalOpen: false,
            copyText: 'Copy'
        }
    },
    computed: {
        endpoint () {
            return `https://models.onsearch.io/${this.currentModel.id}?region=${this.currentModel.region}`
        },
        daysUntilTrialEnds () {
            if (!this.currentModel.trialEnds) {
                return false;
            }
            
            return moment.unix(this.currentModel.trialEnds / 1000).fromNow();
        },
        trialEndsDate () {
            if (!this.currentModel.trialEnds) {
                return false;
            }
            
            return moment.unix(this.currentModel.trialEnds / 1000).format();
        }
    },
    methods: {
        copyResponse () {
            this.$copyText(this.response);
            this.copyText = 'Copied!';

            setTimeout(() => {
                this.copyText = 'Copy'
            }, 1500);
        },
        async stopModel () {
            try {
                this.$root.$emit('loading', true);

                await this.$apollo.mutate({
                    mutation: stopModel,
                    variables: {
                        input: {
                            projectId: this.authenticationState.selectedProject,
                            modelId: this.currentModel.id
                        }
                    }
                });

            } catch (error) {
                this.handleError(error);
            }

            this.$root.$emit('loading', false);
        },
        async testEndpoint () {
            this.$root.$emit('loading', true);

            try {

                axios.interceptors.request.use(req => {
                    req.meta = req.meta || {}
                    req.meta.requestStartedAt = new Date().getTime();
                    return req;
                });

                axios.interceptors.response.use(res => {
                    this.responseTime = `${new Date().getTime() - res.config.meta.requestStartedAt} ms`
                    return res;
                },
                err => {
                    this.responseTime = `${new Date().getTime() - err.config.meta.requestStartedAt} ms`
                    throw err;
                });

                const { data } = await axios.post(this.endpoint, this.payload);

                this.response = data;

            } catch (error) {
                this.response = error.message;
            }

            this.$root.$emit('loading', false);
        }
    },
    mixins: [ LayoutComponents, currentProject, loading, error ],
}
</script>
